import React, { useState, useEffect } from 'react';
import { ChevronDown, HelpCircle, Phone } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Local SEO FAQ with FAQPage JSON-LD structured data for Google rich snippets.
 * Each question targets a long-tail local query.
 */
const FAQ_ITEMS = [
  {
    q: 'Quels sont vos tarifs pour le nettoyage de vitres à Royan ?',
    a: "Nos tarifs pour le nettoyage de vitres à Royan démarrent à 60 € pour un studio ou un T2 (jusqu'à 10 m² de vitrage), 90-140 € pour un T3-T4, et 120-180 € pour une maison plain-pied. Le prix dépend du nombre de fenêtres, des accès et de l'état d'encrassement. Pour les particuliers, vous bénéficiez du crédit d'impôt de 50 % : un devis à 180 € vous revient donc à 90 € après déduction. Demandez un devis gratuit en moins d'une minute via notre formulaire.",
  },
  {
    q: 'Intervenez-vous pour le ménage professionnel à Royan et alentours ?',
    a: "Oui, nous intervenons régulièrement pour le ménage professionnel à Royan, Saint-Palais-sur-Mer, La Tremblade, Marennes et toutes les communes dans un rayon de 25 km. Nous gérons les commerces, bureaux, cabinets médicaux, restaurants, locations saisonnières (Airbnb, Booking) et entreprises. Prestations sur mesure : entretien quotidien, hebdomadaire ou ponctuel. Demandez un devis personnalisé selon vos besoins.",
  },
  {
    q: 'Comment fonctionne le crédit d\'impôt de 50 % pour les particuliers ?',
    a: "Le crédit d'impôt de 50 % s'applique aux prestations de services à la personne effectuées à votre domicile (nettoyage de vitres, ménage, entretien de canapé, etc.) en tant que particulier. SMT Services est adhérent à la coopérative Unipros, ce qui nous permet de vous le faire bénéficier directement, sans démarche compliquée. Concrètement : soit vous payez 100 % puis récupérez 50 % à la déclaration d'impôts, soit vous bénéficiez de l'avance immédiate Urssaf via Unipros et ne payez que votre part de 50 % à chaque intervention. Plafond annuel : 12 000 € de dépenses (6 000 € de crédit d'impôt). Plus de détails sur notre page dédiée crédit d'impôt 50 %.",
  },
  {
    q: 'Dans quelles communes intervenez-vous autour de Royan ?',
    a: "Nous intervenons en priorité sur Royan, Saint-Palais-sur-Mer, Vaux-sur-Mer, Saint-Georges-de-Didonne, La Tremblade, Chaillevette, Marennes, Saujon et Cozes (délai 48-72h). Nous nous déplaçons aussi sans supplément sur Médis, Mornac-sur-Seudre, Étaules, Arvert, Les Mathes, La Palmyre, Saint-Augustin, Breuillet, Saint-Sulpice-de-Royan, Le Gua, Semussac et Meschers-sur-Gironde. Pour les communes hors zone, contactez-nous : un petit forfait kilométrique peut s'appliquer mais reste raisonnable.",
  },
  {
    q: 'Sous combien de temps pouvez-vous intervenir ?',
    a: "Nous envoyons votre devis sous 24h après votre demande. Pour l'intervention elle-même, comptez 48-72h en basse saison (novembre à mars) et 5-8 jours en haute saison (avril à octobre). Pour les urgences (états des lieux, locations saisonnières entre deux locataires), nous proposons un service express selon disponibilité — appelez-nous directement au 06 25 68 48 49.",
  },
  {
    q: 'Faites-vous le nettoyage de vitres pour les particuliers ?',
    a: "Oui, le nettoyage de vitres chez les particuliers est notre cœur de métier. Nous lavons fenêtres, baies vitrées, vérandas, toitures vitrées et encadrements à l'eau osmosée (sans calcaire), avec raclette professionnelle. Résultat sans traces garanti. Et grâce au crédit d'impôt de 50 % services à la personne, votre facture est divisée par deux.",
  },
  {
    q: 'Êtes-vous assuré professionnellement ?',
    a: "Oui, SMT Services dispose d'une assurance Responsabilité Civile Professionnelle qui couvre tout incident éventuel (casse de vitre, dégât matériel) durant nos interventions. Notre certificat est disponible sur simple demande.",
  },
  {
    q: 'Comment demander un devis gratuit ?',
    a: "Trois options : (1) remplissez notre formulaire en ligne en moins d'1 minute, (2) appelez-nous directement au 06 25 68 48 49 du lundi au vendredi, ou (3) envoyez-nous un email à smt.services17t@gmail.com avec une photo du chantier pour un devis plus précis. Réponse sous 24h garanti, sans engagement.",
  },
];

const FAQ = () => {
  const [openIdx, setOpenIdx] = useState(0);

  // Inject FAQPage JSON-LD into <head> for Google rich snippets.
  // Adds AND removes its own script tag — does NOT touch the page-jsonld used by routes.
  useEffect(() => {
    const SCRIPT_ID = 'faq-jsonld';
    const payload = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.a,
        },
      })),
    };
    let el = document.getElementById(SCRIPT_ID);
    if (!el) {
      el = document.createElement('script');
      el.id = SCRIPT_ID;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(payload);
    return () => {
      const node = document.getElementById(SCRIPT_ID);
      if (node) node.remove();
    };
  }, []);

  return (
    <section
      id="faq"
      className="section-padding bg-white relative"
      data-testid="faq-section"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 text-[hsl(32,95%,55%)] font-semibold text-sm uppercase tracking-wider mb-4">
            <HelpCircle className="w-4 h-4" /> Questions fréquentes
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Tout savoir sur nos prestations{' '}
            <span className="text-gradient">à Royan</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-base sm:text-lg">
            Les réponses aux questions que se posent nos clients sur le nettoyage de vitres et le ménage en Charente-Maritime.
          </p>
        </div>

        <div className="space-y-3">
          {FAQ_ITEMS.map((item, idx) => {
            const open = openIdx === idx;
            return (
              <div
                key={idx}
                className="bg-slate-50 hover:bg-slate-100/70 rounded-2xl border border-slate-200 overflow-hidden transition-colors"
              >
                <button
                  type="button"
                  onClick={() => setOpenIdx(open ? -1 : idx)}
                  className="w-full text-left flex items-center justify-between gap-4 p-5 sm:p-6 cursor-pointer"
                  data-testid={`faq-question-${idx}`}
                  aria-expanded={open}
                >
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 leading-snug">
                    {item.q}
                  </h3>
                  <ChevronDown
                    className={`flex-shrink-0 w-5 h-5 text-[hsl(207,90%,54%)] transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
                  />
                </button>
                <div
                  className={`grid transition-all duration-300 ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
                >
                  <div className="overflow-hidden">
                    <p
                      className="px-5 sm:px-6 pb-5 sm:pb-6 text-gray-700 text-sm sm:text-base leading-relaxed"
                      data-testid={`faq-answer-${idx}`}
                    >
                      {item.a}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 bg-gradient-to-br from-[hsl(32,95%,55%)] to-[hsl(32,95%,45%)] rounded-3xl p-8 sm:p-10 text-center text-white shadow-xl">
          <h3 className="text-2xl sm:text-3xl font-bold mb-3">Vous ne trouvez pas votre réponse ?</h3>
          <p className="opacity-95 mb-6 max-w-xl mx-auto">
            Appelez-nous, nous répondons à toutes vos questions et vous orientons vers la meilleure prestation pour votre besoin.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Button asChild size="lg" className="bg-white text-[hsl(32,95%,45%)] hover:bg-orange-50 font-bold">
              <a href="tel:+33625684849" className="flex items-center gap-2" data-testid="faq-cta-call">
                <Phone className="w-4 h-4" /> 06 25 68 48 49
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[hsl(32,95%,45%)] font-semibold"
            >
              <a href="#reservation" data-testid="faq-cta-devis">Demander un devis gratuit</a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
